import { Grid } from '@mui/material';
import * as React from 'react';
import { useMemo } from 'react';
import { Translate } from 'react-localize-redux';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import { TypographyStyled } from '../../../components/general/mini_components';
import Loader from '../../../components/Loader';
import { HeadCell } from '../../../constants/types';
import { useDepartments } from '../../../hooks';
import { useRequests } from '../../../hooks/useRequests';
import SearchBox from '../../../components/general/SearchBox';
import { pharmacyStatusValues, getRequestStatusName } from '../Request/utils';
import { IRequest, RequestStatus, RequestType, ServiceType } from '../Request/types';
import { RequestStatusToChip, statusToColor } from '../Request/RequestTable';
import { fullDateFromPostgresString, researcherFullName, stringDatePostgresToDate } from '../../../utils/index.jsx';
import { formatPharmacyItemsSummary } from './formatPharmacyItemsSummary';

export interface PharmacyRequestTableProps {
    uuidInvestigation: string;
    activeLanguageCode?: string;
    /** @deprecated use requestServiceType — kept for Pharmacy/index compatibility */
    serviceType?: ServiceType;
    requestServiceType?: ServiceType;
    callBackRequestSelected: (request: IRequest) => void;
}

interface PharmacyRow {
    id: number;
    department: string;
    requester: string;
    dateAdded: string;
    requestedItems: string;
    status: JSX.Element;
    statusValue: RequestStatus;
}

export interface PharmacyRequestTableComponentProps {
    requests: IRequest[];
    uuidInvestigation: string;
    activeLanguageCode: string;
    loading?: boolean;
    callBackRequestSelected: (request: IRequest) => void;
}

const PHARMACY_HEAD_CELLS: HeadCell[] = [
    { id: 'id', numeric: false, disablePadding: false, label: <Translate id="pages.hospital.services.request.request_id" /> },
    { id: 'department', label: <Translate id="hospital.departments.department" />, alignment: 'left' },
    { id: 'requester', label: <Translate id="hospital.staff" />, alignment: 'left' },
    { id: 'dateAdded', label: <Translate id="general.date" />, alignment: 'left' },
    { id: 'requestedItems', label: <Translate id="pages.hospital.pharmacy.request.table.requestedItems" />, alignment: 'left' },
    { id: 'status', label: <Translate id="pages.hospital.services.status" />, alignment: 'left' },
];

function buildPharmacyRows(requests: IRequest[], activeLanguageCode: string): PharmacyRow[] {
    return [...requests]
        .sort(
            (reqA, reqB) =>
                stringDatePostgresToDate(reqB.updatedAt).getTime() -
                stringDatePostgresToDate(reqA.updatedAt).getTime(),
        )
        .map((request) => {
            const requesterName = researcherFullName(request.researcher);
            return {
                id: request.id,
                department: request.departmentRequest?.name ?? '',
                requester: requesterName,
                dateAdded: fullDateFromPostgresString(activeLanguageCode, request.createdAt),
                requestedItems: formatPharmacyItemsSummary(request.requestsPharmacy),
                status: <RequestStatusToChip status={request.status} labelPrefix="pages.hospital.pharmacy.request_status" />,
                statusValue: request.status,
            };
        });
}

function filterPharmacyRows(rows: PharmacyRow[], searchText: string, statusFilter: RequestStatus[]): PharmacyRow[] {
    return rows.filter((row) => {
        const statusFiltered = statusFilter.length === 0 || statusFilter.includes(row.statusValue);
        const haystack = [row.requester, row.department, row.requestedItems].join(' ').toLowerCase();
        const searchMatch = searchText === '' || haystack.includes(searchText.toLowerCase());
        return searchMatch && statusFiltered;
    });
}

export const PharmacyRequestTableComponent: React.FC<PharmacyRequestTableComponentProps> = ({
    requests,
    activeLanguageCode,
    loading,
    callBackRequestSelected,
}) => {
    const { departments } = useDepartments();
    const [searchText, setSearchText] = React.useState('');
    const [statusFilter, setStatusFilter] = React.useState<RequestStatus[]>([]);

    const rows = useMemo(() => buildPharmacyRows(requests, activeLanguageCode), [requests, activeLanguageCode]);
    const filteredRows = useMemo(
        () => filterPharmacyRows(rows, searchText, statusFilter),
        [rows, searchText, statusFilter],
    );

    const filterItems = useMemo(
        () =>
            pharmacyStatusValues.map((status) => ({
                label: `pages.hospital.pharmacy.request_status.${getRequestStatusName(status)}`,
                selected: statusFilter.includes(status),
                value: status,
                color: statusToColor(status),
                callBack: () => applyStatusFilter(statusFilter, setStatusFilter, status),
            })),
        [statusFilter],
    );

    if (loading || !departments) {
        return <Loader />;
    }

    if (requests.length === 0) {
        return (
            <div className="text-center">
                <h4>
                    <Translate id="pages.hospital.services.no_requests" />
                </h4>
            </div>
        );
    }

    return (
        <Grid container spacing={2}>
            <SearchBox
                textField={{
                    label: 'hospital.search_box.staff',
                    helperText: 'pages.hospital.pharmacy.request.helperText',
                    callBack: setSearchText,
                }}
                activeFilters={statusFilter}
                filterItems={filterItems}
                filterLabelId="pages.hospital.pharmacy.request.filter"
            />
            <Grid item xs={12}>
                <EnhancedTable
                    selectRow={(id: number) => {
                        const request = requests.find((req) => req.id === id);
                        if (request) {
                            callBackRequestSelected(request);
                        }
                    }}
                    noHeader
                    noSelectable
                    rows={filteredRows}
                    headCells={PHARMACY_HEAD_CELLS}
                    actions={null}
                />
            </Grid>
        </Grid>
    );
};

function applyStatusFilter(
    statusFilter: RequestStatus[],
    setStatusFilter: React.Dispatch<React.SetStateAction<RequestStatus[]>>,
    status: RequestStatus,
) {
    if (statusFilter.includes(status)) {
        setStatusFilter(statusFilter.length === 1 ? [] : statusFilter.filter((s) => s !== status));
    } else {
        setStatusFilter([...statusFilter, status]);
    }
}

export const PharmacyRequestTable: React.FC<PharmacyRequestTableProps> = ({
    uuidInvestigation,
    activeLanguageCode = 'fr-FR',
    callBackRequestSelected,
}) => {
    const { requests, loading, error: requestsError } = useRequests({
        requestServiceType: ServiceType.PHARMACY,
        uuidInvestigation,
        typeRequest: RequestType.PHARMACY,
    });

    if (requestsError) {
        console.error('Pharmacy requests error:', requestsError);
    }

    if (loading || !requests) {
        return <Loader />;
    }

    if (requests.length === 0) {
        return (
            <TypographyStyled variant="body2">
                <Translate id="pages.hospital.services.no_requests" />
            </TypographyStyled>
        );
    }

    return (
        <PharmacyRequestTableComponent
            requests={requests}
            uuidInvestigation={uuidInvestigation}
            activeLanguageCode={activeLanguageCode}
            loading={loading}
            callBackRequestSelected={callBackRequestSelected}
        />
    );
};

/** @deprecated Import from Pharmacy/PharmacyRequestTable */
export const RequestTablePharmacy = PharmacyRequestTable;

export default PharmacyRequestTable;
